import React, { useRef, useState } from 'react';
import { Box, TextField, Chip, IconButton, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { t } from 'i18next';

interface PhoneNumbersFieldProps {
  phoneNumbers: string[];
  onChange: (phoneNumbers: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  isTransfer?: boolean;
  showHelperText?: boolean;
}

// regex for sip numbers number validation
const phoneNumberRegex =
  /^(?:\+[0-9]{7,15}|sips?:[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+(?::\d+)?(?:;[A-Za-z0-9=]+)*|tel:[0-9]+)$/;
// regex for transfer tool number validation
const transferPhoneNumberRegex = /^\+[0-9]{10,15}$/;

const MIN_DIGITS = { transfer: 10, general: 7 };
const MAX_DIGITS = 15;

const isSequentialDigits = (digits: string): boolean => {
  let ascending = true;
  let descending = true;

  for (let i = 1; i < digits.length; i++) {
    const prev = Number(digits[i - 1]);
    const curr = Number(digits[i]);
    if ((curr - prev + 10) % 10 !== 1) ascending = false;
    if ((prev - curr + 10) % 10 !== 1) descending = false;
  }

  return ascending || descending;
};

const isRepeatedDigits = (digits: string): boolean => /^(\d)\1+$/.test(digits);

const getNumberPart = (phoneNumber: string): string =>
  phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

const sanitizeNumericPhoneInput = (value: string): string => {
  const digitsAndPlus = value.replace(/[^\d+]/g, '');
  if (digitsAndPlus.startsWith('+')) {
    return '+' + digitsAndPlus.slice(1).replace(/\+/g, '');
  }
  return digitsAndPlus.replace(/\+/g, '');
};

const validatePhoneNumber = (phoneNumber: string, isTransfer: boolean = false): boolean => {
  const regex = isTransfer ? transferPhoneNumberRegex : phoneNumberRegex;

  if (!regex.test(phoneNumber)) {
    return false;
  }

  const numberPart = getNumberPart(phoneNumber);

  if (isRepeatedDigits(numberPart)) {
    return false;
  }

  if (isSequentialDigits(numberPart)) {
    return false;
  }

  return true;
};

const getLengthError = (phoneNumber: string, isTransfer: boolean): string | undefined => {
  const numberPart = getNumberPart(phoneNumber);
  if (!/^[0-9]+$/.test(numberPart)) {
    return undefined;
  }

  const minDigits = isTransfer ? MIN_DIGITS.transfer : MIN_DIGITS.general;

  if (numberPart.length < minDigits) {
    return t('campaignPage.phoneNumberValidation.tooShort');
  }

  if (numberPart.length > MAX_DIGITS) {
    return t('campaignPage.phoneNumberValidation.tooLong');
  }

  return undefined;
};

const getPhoneNumberError = (
  phoneNumber: string,
  isTransfer: boolean = false,
): string | undefined => {
  const regex = isTransfer ? transferPhoneNumberRegex : phoneNumberRegex;

  if (!regex.test(phoneNumber)) {
    return getLengthError(phoneNumber, isTransfer) ?? t('campaignPage.phoneNumberValidation.invalidFormat');
  }

  const numberPart = getNumberPart(phoneNumber);

  if (isRepeatedDigits(numberPart)) {
    return t('campaignPage.phoneNumberValidation.repeatedDigits');
  }

  if (isSequentialDigits(numberPart)) {
    return t('campaignPage.phoneNumberValidation.sequential');
  }

  return undefined;
};

export const PhoneNumbersField: React.FC<PhoneNumbersFieldProps> = ({
  phoneNumbers = [],
  onChange,
  disabled = false,
  placeholder,
  error = false,
  helperText,
  isTransfer = false,
  showHelperText = true,
}) => {
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [inputError, setInputError] = useState<string | undefined>();

  const handleNumberAdd = (number: string) => {
    const trimmedNumber = number.trim();
    if (trimmedNumber && !phoneNumbers.includes(trimmedNumber)) {
      if (!validatePhoneNumber(trimmedNumber, isTransfer)) {
        const errorMessage = getPhoneNumberError(trimmedNumber, isTransfer);
        setInputError(errorMessage);
        return;
      }
      onChange([...phoneNumbers, trimmedNumber]);
      setCurrentInputValue('');
      setInputError(undefined);
    } else if (phoneNumbers.includes(trimmedNumber)) {
      setInputError(t('campaignPage.phoneNumberValidation.alreadyAdded'));
    }
  };

  const handleNumberRemove = (index: number) => {
    const newNumbers = [...phoneNumbers];
    newNumbers.splice(index, 1);
    onChange(newNumbers);
  };

  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (currentInputValue.trim()) {
        handleNumberAdd(currentInputValue);
        if (phoneNumberInputRef.current) {
          phoneNumberInputRef.current.value = '';
        }
      }
    }
  };

  const handleAddNumberClick = () => {
    if (phoneNumberInputRef.current?.value) {
      handleNumberAdd(phoneNumberInputRef.current.value);
      phoneNumberInputRef.current.value = '';
    }
  };

  const handleInputChange = (value: string) => {
    const nextValue = isTransfer ? sanitizeNumericPhoneInput(value) : value;
    setCurrentInputValue(nextValue);
    setInputError(undefined);
    if (nextValue.trim()) {
      const error = getPhoneNumberError(nextValue.trim(), isTransfer);
      setInputError(error);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom></Typography>
      <TextField
        fullWidth
        placeholder={placeholder || t('campaignPage.transferCallPlaceholder')}
        onKeyPress={handleNumberKeyPress}
        disabled={disabled}
        inputRef={phoneNumberInputRef}
        value={currentInputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        error={error || !!inputError}
        helperText={showHelperText ? helperText || inputError : undefined}
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={handleAddNumberClick}
              disabled={disabled || !!inputError || !currentInputValue.trim()}>
              <AddIcon />
            </IconButton>
          ),
        }}
      />

      {phoneNumbers.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {phoneNumbers.map((number, index) => (
            <Chip
              key={index}
              label={number}
              onDelete={() => handleNumberRemove(index)}
              disabled={disabled}
              color={validatePhoneNumber(number, isTransfer) ? 'primary' : 'error'}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
